// © Microsoft Corporation. All rights reserved.

import { canUseDOM } from "@msnews/isomorphic-util";

const pageTimings = canUseDOM() ? (window._pageTimings || (window._pageTimings = {})) : {};
const secondaryPageTimings = canUseDOM() ? (window._secondaryPageTimings || (window._secondaryPageTimings = {})) : {};

/**
 * The key to store the beginning time of a request on server-side
 * In browsers, `time orgin` is a standard time which is considered to be the beginning of the current document's lifetime. Ref: https://docs.w3cub.com/dom/domhighrestimestamp#The_time_origin
 * On the server-side, `time origin` is considered to be the beginning of the Node process
 * So, to log the start time of something (during processing a request) on the server-side, we need to know the beginning time for the current request, which could be get from here.
 */
const ssrTimeOrigin = "ssrTimeOrigin";

/**
 * Gets current window._pageTimings object
 */
export function getPageTimings(): object {
    return pageTimings;
}

/**
 * Gets current window._secondaryPageTimings object
 */
export function getSecondaryPageTimings(): object {
    return secondaryPageTimings;
}

/**
 * Creates or updates a new counter under the page timings
 * @param counterName - the name of the counter to increment.
 * @param isPreTTVRCounter - Flag to indicate whether the counting should be limited to pre TTVR
 */
export function incrementCounter(counterName: string, isPreTTVRCounter = true): void {

    if (!counterName
        || !pageTimings
        // if isPreTTVRCounter is true, then only log the counter if TTVR marker is not present yet
        || (isPreTTVRCounter && pageTimings["TTVR"])) {

        return;
    }

    const existingCount = pageTimings[counterName] || (pageTimings[counterName] = 0);
    pageTimings[counterName] = existingCount + 1;
}

/**
 * Reset pageTimings object
 */
export function resetPageTimings(): void {
    Object.keys(pageTimings).forEach(key => delete pageTimings[key]);
    Object.keys(secondaryPageTimings).forEach(key => delete secondaryPageTimings[key]);

    if (!canUseDOM()) {
        pageTimings[ssrTimeOrigin] = performance.now();
    }
}

/**
 * @returns the elapsed time since the `time origin`, in milliseconds
 */
export function getElapsedSinceTimeOrigin(): number {
    if (canUseDOM()) {
        return performance.now();
    } else {
        return performance.now() - pageTimings[ssrTimeOrigin];
    }
}